import {config} from "../config/config.ts";

/**
 * Client-side friction reporter — feeds Admin → Client Errors. Captures the "clicked and nothing
 * happened" class of problems Bugsnag doesn't surface:
 *  - js_error / unhandled_rejection: uncaught exceptions (also still go to Bugsnag)
 *  - api_error: 5xx / network failures from the central apiCall funnel
 *  - rage_click: 4+ rapid clicks on the same element — the user is stuck
 *
 * Deliberately conservative: hard per-session cap, per-message dedupe, and noise filters
 * (extensions, ads, cross-origin "Script error.") so the log stays signal, not spam.
 */

const SESSION_CAP = 12;
let sent = 0;
const seen = new Set<string>();

const NOISE = [
    /^Script error\.?$/,                       // opaque cross-origin errors
    /ResizeObserver loop/,                     // benign browser noise
    /adsbygoogle|googlesyndication|pagead/i,   // ad units erroring
    /chrome-extension:|moz-extension:/,        // user extensions
    // Stale-deploy chunk reloads — Chrome/Firefox and Safari wordings of the same event.
    /Loading chunk .* failed|Failed to fetch dynamically imported module|Importing a module script failed/,
    // Microsoft Outlook's SafeLink email scanner breaking its own injected scripts — famous phantom.
    /Object Not Found Matching Id/,
    // The Facebook / Instagram in-app browser injects an autofill hook that references itself
    // before it exists — the page never touched it.
    /_AutofillCallbackHandler/,
    // A fetch cancelled by navigation: normal app behaviour, the same reason apiCall drops aborts.
    /^AbortError$/,
];

/**
 * Opt-in escape hatch for testing this reporter.
 *
 * `navigator.webdriver` is true in every automated browser, so isBot() suppresses ALL reports
 * under automation — which means none of this file can be exercised by a test without the flag.
 * Set localStorage.sp_error_reporter_force = '1' to report anyway. Deliberately opt-in and
 * localStorage-scoped: real crawlers never set it, so production filtering is unchanged.
 */
function forcedOn(): boolean {
    try {
        return localStorage.getItem('sp_error_reporter_force') === '1';
    } catch {
        return false;
    }
}

/** Crawlers execute JS too (Googlebot renders pages) — their quirks aren't user friction. */
function isBot(): boolean {
    if (forcedOn()) return false;
    try {
        return (navigator as any).webdriver === true
            || /bot|crawler|spider|crawling|headless|lighthouse|slurp|bingpreview/i.test(navigator.userAgent);
    } catch {
        return false;
    }
}

function report(type: 'js_error' | 'unhandled_rejection' | 'api_error' | 'rage_click', message: string, extra: {stack?: string; meta?: Record<string, any>} = {}) {
    try {
        message = String(message || '').slice(0, 500);
        if (!message || sent >= SESSION_CAP || isBot()) return;
        if (NOISE.some((re) => re.test(message) || (extra.stack && re.test(extra.stack)))) return;
        const key = type + '|' + message;
        if (seen.has(key)) return;
        seen.add(key);
        sent++;

        const token = localStorage.getItem('jwt_token');
        fetch(config.apiUrl + '/client-error', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...(token ? {Authorization: 'Bearer ' + token} : {}),
            },
            body: JSON.stringify({
                type,
                message,
                url: window.location.href.split('#')[0].slice(0, 500),
                stack: extra.stack ? String(extra.stack).slice(0, 4000) : undefined,
                meta: extra.meta,
            }),
            keepalive: true,
        }).catch(() => {});
    } catch {
        // the reporter must never throw
    }
}

/**
 * Called from the route error boundary. React hands a render or loader error to the boundary
 * instead of window.onerror, so without this the "Something went wrong" screen a user sees
 * never reaches Admin → Client Errors and cannot be diagnosed after the fact.
 */
export function reportRenderError(error: unknown) {
    const err = error as any;
    report('js_error', 'render: ' + (err?.message || String(error)), {
        stack: err?.stack,
        meta:  {boundary: true},
    });
}
/** Called from apiCall for 5xx responses and network-level failures. */
export function reportApiFailure(path: string, status: number, detail?: string) {
    if (path === 'client-error') return; // never report about ourselves
    // Deliberately cancelled requests are normal app behaviour (autocomplete typing, navigation,
    // react-query cancellation) — Safari says "Fetch is aborted", Chrome "The user aborted a request".
    if (detail && /abort/i.test(detail)) return;
    // The periodic app-version poll dies whenever iOS backgrounds the tab — pure noise.
    if (path === 'version' && !status) return;
    report('api_error', `${path} → ${status || 'network failure'}${detail ? `: ${detail.slice(0, 200)}` : ''}`, {
        meta: {path, status},
    });
}

/** Short human-readable identity for a clicked element, e.g. "button#save.btn-primary 'Save draw'". */
function describeElement(el: Element): string {
    const tag = el.tagName.toLowerCase();
    const id = el.id ? `#${el.id}` : '';
    const cls = typeof el.className === 'string' && el.className
        ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
        : '';
    const text = (el.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 40);
    return `${tag}${id}${cls}${text ? ` '${text}'` : ''}`.slice(0, 160);
}

/**
 * What a click landed on, reduced to the few facts that decide whether it is worth reporting.
 * Kept separate from the DOM so the rule can be tested (the frontend test env has no document).
 */
export type ClickShape = {
    /** Matched a real control: button, link, role=button/tab, submit, summary, label. */
    interactive: boolean;
    /** Computed cursor — a div styled `cursor: pointer` is a control to the person clicking it. */
    cursor: string;
    /** The click resolved to <html>/<body>: it hit no control at all. */
    isRoot: boolean;
    /** Inside the Google consent dialog (fc-*), whose overlay ignores clicks by design. */
    inConsent: boolean;
    /** Typing and selection surfaces produce rapid legitimate clicks. */
    editable: boolean;
    /** A control built to be pressed repeatedly (a +/- stepper, marked data-rapid). */
    rapid?: boolean;
};

/**
 * Rage clicks are only worth logging on things the person could reasonably believe are buttons.
 * Tapping a heading, a paragraph or the page background while waiting says nothing about a
 * broken control — and those made up a third of the log, one of them dragging the page's whole
 * stylesheet along as its "text".
 */
export function shouldReportRageClick(shape: ClickShape): boolean {
    if (shape.isRoot || shape.inConsent || shape.editable || shape.rapid) return false;

    return shape.interactive || shape.cursor === 'pointer';
}

const INTERACTIVE = 'button, a, [role="button"], [role="tab"], [role="menuitem"], input[type="submit"], summary, label';

let installed = false;

export function installErrorReporter() {
    if (installed || typeof window === 'undefined') return;
    installed = true;

    window.addEventListener('error', (e) => {
        // Resource-load errors (img, script) surface as events without an Error — skip those.
        if (!e.message) return;
        report('js_error', e.message, {stack: e.error?.stack, meta: {source: e.filename, line: e.lineno}});
    });

    window.addEventListener('unhandledrejection', (e) => {
        const reason: any = e.reason;
        report('unhandled_rejection', reason?.message || reason?.error || String(reason ?? 'unknown'), {stack: reason?.stack});
    });

    // Rage clicks: the same interactive element clicked 4+ times within a short burst usually means
    // "I pressed it and nothing happened". One report per element per session.
    let lastTarget: Element | null = null;
    let clickCount = 0;
    let firstClickAt = 0;
    let burstUrl = '';
    const reportedTargets = new WeakSet<Element>();

    window.addEventListener('click', (e) => {
        const raw = e.target as Element | null;
        if (!raw || !(raw instanceof Element)) return;
        // Google Funding Choices (the GDPR consent CMP loaded in __root.tsx) renders its dialog with
        // `fc-`-prefixed classes. Clicking a modal's overlay does nothing BY DESIGN, so EU visitors
        // batting at the consent wall produced most of the rage-click log — third-party markup we
        // can neither fix nor act on. Matched on the subtree, not the message, so the dialog's
        // unclassed descendants (its body copy) are excluded too.
        // Attribute the click to the nearest interactive ancestor so text/icon clicks group together.
        const interactive = raw.closest(INTERACTIVE);
        const el = (interactive || raw) as Element;
        let cursor = '';
        try {
            cursor = window.getComputedStyle(el).cursor;
        } catch { /* pre-render / detached node */ }
        if (! shouldReportRageClick({
            interactive: interactive !== null,
            cursor,
            isRoot:      el === document.documentElement || el === document.body,
            inConsent:   raw.closest('[class^="fc-"], [class*=" fc-"]') !== null,
            editable:    el.matches('input, textarea, select, [contenteditable="true"]'),
            rapid:       el.closest('[data-rapid]') !== null,
        })) return;

        const now = Date.now();
        if (el === lastTarget && now - firstClickAt < 1600) {
            clickCount++;
            if (clickCount >= 4 && !reportedTargets.has(el)) {
                reportedTargets.add(el);
                report('rage_click', `Rage click: ${describeElement(el)}`, {
                    // navigated: the burst moved the app somewhere, so the control did work and
                    // the person was only impatient — the difference the count alone can't show.
                    meta: {
                        clicks:    clickCount,
                        disabled:  (el as any).disabled === true,
                        navigated: location.href !== burstUrl,
                    },
                });
            }
        } else {
            lastTarget = el;
            clickCount = 1;
            firstClickAt = now;
            burstUrl = location.href;
        }
    }, {capture: true, passive: true});
}
